import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  PATHFINDER_TOOL_GROUPS,
  PathfinderToolItem,
} from './PathfinderToolRail';

export interface PathfinderToolPaletteProps {
  open: boolean;
  onClose: () => void;
  tools: PathfinderToolItem[];
  activeToolId: string;
  onSelect: (id: string) => void;
}

const PathfinderToolPalette: React.FC<PathfinderToolPaletteProps> = ({
  open,
  onClose,
  tools,
  activeToolId,
  onSelect,
}) => {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? tools.filter((t) => t.label.toLowerCase().includes(q) || t.id.includes(q))
      : tools;

    // Same order as the rail: Stats → Narrate → Find.
    const order = new Map(PATHFINDER_TOOL_GROUPS.map((g, i) => [g.id, i]));
    return [...base].sort((a, b) => {
      const ga = order.get(a.group) ?? 99;
      const gb = order.get(b.group) ?? 99;
      if (ga !== gb) return ga - gb;
      return a.label.localeCompare(b.label);
    });
  }, [tools, query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlight(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    setHighlight((h) => (filtered.length === 0 ? 0 : Math.min(h, filtered.length - 1)));
  }, [filtered.length]);

  const selectAt = (index: number) => {
    const tool = filtered[index];
    if (!tool) return;
    onSelect(tool.id);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (filtered.length === 0 ? 0 : (h + 1) % filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) =>
        filtered.length === 0 ? 0 : (h - 1 + filtered.length) % filtered.length,
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectAt(highlight);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const groupLabel = (groupId: string) =>
    PATHFINDER_TOOL_GROUPS.find((g) => g.id === groupId)?.label ?? groupId;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          bgcolor: (t) => alpha(t.palette.background.paper, 0.94),
          backdropFilter: 'blur(12px)',
          border: 1,
          borderColor: 'divider',
          backgroundImage: 'none',
          mt: { xs: 2, sm: 8 },
          alignSelf: 'flex-start',
        },
      }}
    >
      <DialogContent sx={{ p: 1.5, pt: 1.5 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          placeholder="Jump to tool…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'action.hover',
            },
          }}
        />
        {filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
            No tools match “{query.trim()}”
          </Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {filtered.map((tool, index) => {
              const selected = tool.id === activeToolId;
              const highlighted = index === highlight;
              return (
                <ListItemButton
                  key={tool.id}
                  selected={highlighted || selected}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => selectAt(index)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.25,
                    '&.Mui-selected': {
                      bgcolor: (t) =>
                        alpha(t.palette.primary.main, highlighted ? 0.28 : 0.16),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: selected ? 'primary.main' : 'text.secondary' }}>
                    {tool.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={tool.label}
                    secondary={groupLabel(tool.group)}
                    primaryTypographyProps={{ fontWeight: selected ? 600 : 400, fontSize: '0.875rem' }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
        <Box sx={{ px: 1, pt: 1, display: 'flex', gap: 2 }}>
          <Typography variant="caption" color="text.disabled">
            ↑↓ navigate
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Enter select
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Esc close
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PathfinderToolPalette;
